import { useCallback, useState } from 'react';
import {
  usePaymentMethod,
  usePaymentEvent,
  useBillingHistory,
  type SubscriptionUpdatePreviewT,
} from '@mentimeter/billing';
import { PurchaseSummary } from '@mentimeter/checkout-ui';
import { useUser } from '@mentimeter/user';
import { PaymentMethodType } from '@mentimeter/http-clients';
import {
  trackAddLicenses,
  trackPlanUpgrade,
} from '@mentimeter/google-tracking';
import {
  DefaultPurchaseErrorMessage,
  trackSubmitting,
  updateStripe,
  updateSubscription,
} from '../data-hooks/hooks';
import type { CheckoutModalType } from '../types';

interface PurchaseSummaryDataHandlerT {
  isLoading: boolean;
  type: CheckoutModalType;
  numberOfLicenses: number;
  invoicePreview: SubscriptionUpdatePreviewT | undefined;
  onSubmitSuccess: (receiptUrl: string) => void;
}

export const PurchaseSummaryDataHandler = ({
  isLoading,
  type,
  invoicePreview,
  numberOfLicenses,
  onSubmitSuccess,
}: PurchaseSummaryDataHandlerT) => {
  const { user } = useUser();
  const { paymentMethod } = usePaymentMethod(!user || user?.isFreeUser);

  const { mutate: mutateBillingHistory } = useBillingHistory();

  const [purchaseLoading, setPurchaseLoading] = useState<boolean>(false);
  const [purchaseError, setPurchaseError] = useState<string>();

  // Listen for subscription update to be done and fetch the newest receipt
  usePaymentEvent('subscription.updated', () => {
    mutateBillingHistory().then((newReceipts) => {
      if (newReceipts?.[0]?.invoice_url)
        onSubmitSuccess(newReceipts[0].invoice_url);
      setPurchaseLoading(false);
    });
  });

  const submitPurchase = useCallback(async () => {
    if (!paymentMethod) {
      return setPurchaseError('Please add a payment method');
    }
    setPurchaseLoading(true);

    if (!invoicePreview) {
      setPurchaseLoading(true);

      if (!isLoading) {
        setPurchaseLoading(false);
        setPurchaseError(DefaultPurchaseErrorMessage);
      }

      return;
    }

    trackSubmitting();

    const newData = {
      licenses: invoicePreview.purchaseItem.quantity,
      plan_category:
        type === 'licenses'
          ? invoicePreview.prorationItem.category
          : invoicePreview.purchaseItem.category,
      proration_date: invoicePreview.prorationDate,
    };

    const [errorResponse, successResponse] = await updateSubscription(newData);

    if (errorResponse) {
      setPurchaseLoading(false);
      return setPurchaseError(DefaultPurchaseErrorMessage);
    }

    if (successResponse?.data.payment_intent_client_secret) {
      const errorMessage = await updateStripe(
        successResponse.data.payment_intent_client_secret,
        paymentMethod,
      );

      const numberOfLicensesAdded =
        numberOfLicenses - invoicePreview.prorationItem.quantity;

      const isPlanUpdate = type === 'plan';
      const track = isPlanUpdate ? trackPlanUpgrade : trackAddLicenses;
      const quantity = isPlanUpdate ? numberOfLicenses : numberOfLicensesAdded;
      const pricePerLicense = invoicePreview.totalExclTax / quantity;
      const itemPrice = pricePerLicense / invoicePreview.subUnitToUnit;
      const totalPriceExclTax =
        invoicePreview.totalExclTax / invoicePreview.subUnitToUnit;

      track({
        transaction_id: successResponse.data.request_id,
        value: totalPriceExclTax,
        currency: invoicePreview.currency,
        item_name: invoicePreview.purchaseItem.category,
        item_price: itemPrice,
        quantity,
        payment_method: paymentMethod?.type,
      });

      if (errorMessage) {
        setPurchaseLoading(false);
        return setPurchaseError(errorMessage);
      }
    }
  }, [paymentMethod, invoicePreview, type, isLoading, numberOfLicenses]);

  return (
    <PurchaseSummary
      isLoading={isLoading}
      purchaseLoading={purchaseLoading}
      paymentMethod={paymentMethod?.type ?? PaymentMethodType.Card}
      invoicePreview={invoicePreview}
      purchaseError={purchaseError ?? ''}
      onSubmit={submitPurchase}
    />
  );
};
